import React, { useState, useEffect } from 'react';
import dataService from '../services/dataService';
import { useToast } from '../context/ToastContext';
import {
  Megaphone, MessageCircle, Mail, Search, Users,
  MessageSquare, FileText, Clock, CheckSquare, Square, Send
} from "lucide-react";
import './MarketingScreen.css';

function MarketingScreen() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [broadcasting, setBroadcasting] = useState(false);
  const { addToast } = useToast();

  const [messageTemplate, setMessageTemplate] = useState(
    "Hello [Name],\n\nThis is a follow-up from Promotional LLC-FZ regarding your recent inquiry. We are excited to assist you with your marketing and promotional needs.\n\nHow can we help you today?\n\nBest regards,\nPromotional LLC-FZ Team\n📞 052-3622578"
  );
  const [emailSubject, setEmailSubject] = useState("Follow-up from Promotional LLC-FZ");

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await dataService.getCustomers();
        setCustomers(Array.isArray(res.data.data) ? res.data.data : []);
      } catch (err) {
        addToast("Failed to fetch customers", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [addToast]);

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.companyName && c.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedCustomers = customers.filter(c => selectedIds.has(c._id));

  // Individual actions
  const handleWhatsApp = (customer) => {
    const phoneNum = customer.whatsapp || customer.phone;
    if (!phoneNum) { addToast("No phone number for this customer", "error"); return; }
    const cleanPhone = phoneNum.replace(/\D/g, '');
    const message = messageTemplate.replace(/\[Name\]/g, customer.name);
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleEmail = (customer) => {
    if (!customer.email) { addToast("No email for this customer", "error"); return; }
    const body = messageTemplate.replace(/\[Name\]/g, customer.name);
    window.open(`mailto:${customer.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(body)}`);
  };

  // Selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCustomers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCustomers.map(c => c._id)));
    }
  };

  // WhatsApp Broadcast — opens each customer tab with 300ms delay to avoid popup block
  const handleWhatsAppBroadcast = async () => {
    if (selectedCustomers.length === 0) { addToast("Select at least one customer", "error"); return; }
    const valid = selectedCustomers.filter(c => c.whatsapp || c.phone);
    if (valid.length === 0) { addToast("No selected customers have phone numbers", "error"); return; }
    setBroadcasting(true);
    addToast(`Opening WhatsApp for ${valid.length} customers...`, "success");
    for (let i = 0; i < valid.length; i++) {
      await new Promise(r => setTimeout(r, 350));
      const phone = (valid[i].whatsapp || valid[i].phone).replace(/\D/g, '');
      const msg = messageTemplate.replace(/\[Name\]/g, valid[i].name);
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    }
    setBroadcasting(false);
  };

  // Email Broadcast — opens mailto with BCC list for quick sending via mail client
  const handleEmailBroadcast = () => {
    if (selectedCustomers.length === 0) { addToast("Select at least one customer", "error"); return; }
    const emails = selectedCustomers.filter(c => c.email).map(c => c.email);
    if (emails.length === 0) { addToast("No selected customers have email addresses", "error"); return; }
    // Generic broadcast body (no personalisation for BCC)
    const body = messageTemplate.replace(/\[Name\]/g, "Valued Customer");
    const mailto = `mailto:?bcc=${emails.join(',')}&subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto);
    addToast(`Email broadcast opened for ${emails.length} recipients`, "success");
  };

  const allSelected = filteredCustomers.length > 0 && selectedIds.size === filteredCustomers.length;

  return (
    <div className="marketing-container">
      <div className="marketing-header">
        <div className="header-content">
          <div className="header-title">
            <div className="icon-badge"><Megaphone size={24} color="white" /></div>
            <div>
              <h1>Marketing &amp; Broadcast</h1>
              <p>WhatsApp &amp; Email broadcast to selected customers</p>
            </div>
          </div>
          {/* Broadcast action buttons */}
          {selectedIds.size > 0 && (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ background: '#7C3AED', color: '#fff', borderRadius: '20px', padding: '4px 12px', fontSize: '13px', fontWeight: '600' }}>
                {selectedIds.size} selected
              </span>
              <button
                className="action-btn whatsapp-btn"
                onClick={handleWhatsAppBroadcast}
                disabled={broadcasting}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: '#25D366', color: '#fff', fontWeight: '600' }}
              >
                <Send size={16} /> {broadcasting ? 'Sending...' : 'WhatsApp Broadcast'}
              </button>
              <button
                className="action-btn email-btn"
                onClick={handleEmailBroadcast}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: '#2563eb', color: '#fff', fontWeight: '600' }}
              >
                <Mail size={16} /> Email Broadcast
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="marketing-grid">
        <div className="marketing-left">
          {/* Template Configuration */}
          <div className="marketing-card template-card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileText size={20} color="var(--primary-color)" />
                <h2>Message Template</h2>
              </div>
              <span className="helper-text">Use [Name] as placeholder</span>
            </div>
            <div className="card-body">
              <div className="input-group">
                <label>Email Subject Line</label>
                <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Enter professional subject line" />
              </div>
              <div className="input-group">
                <label>Message Content (WhatsApp &amp; Email Body)</label>
                <textarea value={messageTemplate} onChange={(e) => setMessageTemplate(e.target.value)} rows={9} placeholder="Write your broadcast message here..." />
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="marketing-info-card">
            <div className="info-item">
              <div className="info-icon"><MessageSquare size={18} /></div>
              <div className="info-text">
                <strong>WhatsApp Broadcast</strong>
                <p>Opens WhatsApp for each selected customer with personalised message. Allow popups in browser.</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon"><Mail size={18} /></div>
              <div className="info-text">
                <strong>Email Broadcast</strong>
                <p>Opens your mail client with all selected emails as BCC recipients for one-click sending.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="marketing-right">
          <div className="marketing-card customer-card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Users size={20} color="var(--primary-color)" />
                <h2>Target Customers</h2>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="search-bar">
                  <Search size={16} />
                  <input type="text" placeholder="Search name or company..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <button
                  onClick={toggleSelectAll}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: allSelected ? '#7C3AED' : '#fff', color: allSelected ? '#fff' : '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap' }}
                >
                  {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>
            <div className="card-body no-padding">
              <div className="customer-list-container">
                {loading ? (
                  <div className="list-loading">
                    <Clock className="spin" size={24} />
                    <p>Loading customer database...</p>
                  </div>
                ) : filteredCustomers.length === 0 ? (
                  <div className="empty-state">
                    <Users size={48} color="#E5E7EB" />
                    <p>No customers match your search</p>
                  </div>
                ) : (
                  <div className="customer-list">
                    {filteredCustomers.map(customer => (
                      <div
                        key={customer._id}
                        className={`customer-item ${selectedIds.has(customer._id) ? 'selected-customer' : ''}`}
                        style={{ background: selectedIds.has(customer._id) ? '#f5f3ff' : undefined, borderLeft: selectedIds.has(customer._id) ? '3px solid #7C3AED' : '3px solid transparent' }}
                      >
                        {/* Checkbox */}
                        <div onClick={() => toggleSelect(customer._id)} style={{ cursor: 'pointer', marginRight: '8px', flexShrink: 0 }}>
                          {selectedIds.has(customer._id)
                            ? <CheckSquare size={20} color="#7C3AED" />
                            : <Square size={20} color="#9CA3AF" />}
                        </div>
                        <div className="customer-info" style={{ flex: 1 }}>
                          <div className="customer-avatar">{customer.name.charAt(0)}</div>
                          <div className="customer-meta">
                            <h3>{customer.name}</h3>
                            <p>{customer.companyName || 'Individual'} {customer.phone ? `· ${customer.phone}` : ''}</p>
                          </div>
                        </div>
                        <div className="customer-actions">
                          <button className="action-btn whatsapp-btn" onClick={() => handleWhatsApp(customer)} title="Send WhatsApp">
                            <MessageCircle size={16} /><span>WhatsApp</span>
                          </button>
                          <button className="action-btn email-btn" onClick={() => handleEmail(customer)} title="Send Email">
                            <Mail size={16} /><span>Email</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarketingScreen;
